import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { LogoMark } from '../components/Logo';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { INDUSTRIAS, SECTORES, TAMANOS } from '../lib/constants';

const STEPS = [
  { id: 'industria', label: 'Industria' },
  { id: 'tamano', label: 'Tamaño y sector' },
  { id: 'confirmacion', label: 'Confirmar' },
];

export default function Onboarding() {
  const { organization, updateOrganization, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [hasDiag, setHasDiag] = useState(false);

  useEffect(() => {
    api.get('/diagnostic/latest')
      .then((r) => setHasDiag(!!r.data))
      .catch(() => setHasDiag(false));
  }, []);

  const [form, setForm] = useState({
    razon_social: organization?.razon_social || '',
    rut: organization?.rut || '',
    giro: organization?.giro || '',
    website: organization?.website || '',
    industria: organization?.industria && organization.industria !== 'otro' ? organization.industria : '',
    tamano: organization?.tamano || '',
    sector: organization?.sector || 'privado',
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === 'string' ? v : v.target.value }));

  const canNext = useMemo(() => {
    if (step === 0) return !!form.industria;
    if (step === 1) return !!form.tamano && !!form.sector;
    return true;
  }, [step, form]);

  const handleNext = async () => {
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setSubmitting(true);
    try {
      const { data: updated } = await api.put('/organizations/me', form);
      updateOrganization(updated);
      await api.post('/organizations/complete-onboarding');
      updateOrganization({ ...updated, onboarding_complete: true });
      toast.success(hasDiag
        ? 'Perfil completo. Su diagnóstico ya está guardado en el panel.'
        : 'Perfil de organización completo. Iniciemos el diagnóstico.');
      navigate(hasDiag ? '/app/dashboard' : '/app/diagnostico');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos guardar los datos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={28} />
          <div>
            <div className="pv-eyebrow">Bienvenido, {user?.nombre}</div>
            <div className="text-lg font-semibold text-[#1C2329]">Configuremos su organización en 2 minutos</div>
          </div>
        </div>

        {/* Stepper */}
        <div className="grid grid-cols-3 gap-3 mb-8" data-testid="onboarding-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex flex-col gap-2">
              <div className={`h-1 rounded-full ${i <= step ? 'bg-[#2A9B68]' : 'bg-[#E5E7EB]'}`} />
              <div className="flex items-center gap-2 text-[12px]">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold ${i < step ? 'bg-[#2A9B68] text-white' : i === step ? 'border border-[#2A9B68] text-[#2A9B68]' : 'border border-[#E5E7EB] text-[#9CA3AF]'}`}>
                  {i < step ? <Check size={11} strokeWidth={2.5} /> : i + 1}
                </span>
                <span className={i <= step ? 'text-[#1C2329] font-medium' : 'text-[#9CA3AF]'}>{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="pv-card p-8 md:p-10 animate-fade-in" data-testid={`onboarding-step-${STEPS[step].id}`}>
          {step === 0 && (
            <>
              <h2 className="text-xl font-semibold text-[#1C2329]">¿En qué industria opera?</h2>
              <p className="text-sm text-[#6B7280] mt-1">Usamos esto para ajustar plantillas por sector: retail, salud, fintech, etc.</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {INDUSTRIAS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('industria')(opt.value)}
                    className={`text-left p-4 rounded-md border transition-colors ${form.industria === opt.value ? 'border-[#2A9B68] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'}`}
                    data-testid={`ob-industria-${opt.value}`}
                  >
                    <div className={`text-[15px] font-medium ${form.industria === opt.value ? 'text-[#065F46]' : 'text-[#1C2329]'}`}>
                      {opt.label}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="text-xl font-semibold text-[#1C2329]">Tamaño y sector</h2>
              <p className="text-sm text-[#6B7280] mt-1">Priviax adapta el plan según la escala de su organización.</p>
              <div className="mt-6 space-y-6">
                <div>
                  <div className="pv-eyebrow mb-3">Personas en la organización</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {TAMANOS.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => set('tamano')(t.value)}
                        className={`p-3 rounded-md border text-center text-sm font-medium transition-colors ${form.tamano === t.value ? 'border-[#2A9B68] bg-[#ECFDF5] text-[#065F46]' : 'border-[#E5E7EB] bg-white text-[#1C2329] hover:bg-[#FAFBFC]'}`}
                        data-testid={`ob-tamano-${t.value}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="pv-eyebrow mb-3">Sector</div>
                  <div className="grid grid-cols-3 gap-3 max-w-md">
                    {SECTORES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => set('sector')(s.value)}
                        className={`p-3 rounded-md border text-sm font-medium transition-colors ${form.sector === s.value ? 'border-[#2A9B68] bg-[#ECFDF5] text-[#065F46]' : 'border-[#E5E7EB] bg-white text-[#1C2329] hover:bg-[#FAFBFC]'}`}
                        data-testid={`ob-sector-${s.value}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-xl font-semibold text-[#1C2329]">Confirme la información</h2>
              <p className="text-sm text-[#6B7280] mt-1">Puede editarla luego en Configuración.</p>
              <div className="mt-6 border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]" data-testid="ob-summary">
                <SummaryRow k="Razón social" v={form.razon_social} />
                <SummaryRow k="RUT" v={form.rut} />
                <SummaryRow k="Giro" v={form.giro} />
                <SummaryRow k="Sitio web" v={form.website || '—'} />
                <SummaryRow k="Industria" v={INDUSTRIAS.find((i) => i.value === form.industria)?.label} />
                <SummaryRow k="Tamaño" v={TAMANOS.find((t) => t.value === form.tamano)?.label} />
                <SummaryRow k="Sector" v={SECTORES.find((s) => s.value === form.sector)?.label} />
              </div>
              <div className="mt-6 p-4 rounded-md bg-[#FFF7E6] border border-[#FBBF24]/40 text-[13px] text-[#92400E]">
                {hasDiag
                  ? 'Al confirmar, iremos directo a su panel: el diagnóstico que hizo ya está guardado con su score y plan de acción.'
                  : 'Al confirmar, iniciaremos el diagnóstico inicial. Toma entre 10 y 15 minutos y su score se calculará automáticamente.'}
              </div>
            </>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="pv-btn-secondary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="ob-back"
            >
              <ArrowLeft size={14} /> Volver
            </button>
            <button
              type="button"
              disabled={!canNext || submitting}
              onClick={handleNext}
              className="pv-btn-primary flex items-center gap-2"
              data-testid="ob-next"
            >
              {step === STEPS.length - 1
                ? (submitting ? 'Guardando…' : (hasDiag ? 'Confirmar e ir al panel' : 'Confirmar y comenzar diagnóstico'))
                : 'Continuar'}
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-[12px] uppercase tracking-wider text-[#6B7280]">{k}</span>
      <span className="text-sm text-[#1C2329] font-medium">{v || '—'}</span>
    </div>
  );
}
